import { useState, useEffect, useCallback } from 'react';
import { safeJson, networkMessage } from '../apiHelpers';
import PiPrice from './PiPrice';
import { t } from '../i18n';

const API_BASE = '/api/meetup';

function getAuthToken() {
  return localStorage.getItem('token') || '';
}

function authHeaders() {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Token ${token}` } : {}),
  };
}

const colors = {
  bg: '#F7F5F0',
  surface: '#fff',
  border: '#E3E1D9',
  text: '#1F1E1B',
  textMuted: '#5F5E5A',
  primary: '#0F6E56',
  primaryBg: '#E1F5EE',
  danger: '#A32D2D',
};

const CATEGORIES = [
  ['tech', 'Tech & Programming'], ['business', 'Business & Networking'],
  ['sports', 'Sports & Fitness'], ['arts', 'Arts & Culture'], ['music', 'Music'],
  ['food', 'Food & Drink'], ['education', 'Education & Learning'], ['social', 'Social & Hobbies'], ['other', 'Other'],
];

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }) + ' · ' + d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function EventCard({ event, onOpen, piRate }) {
  return (
    <div onClick={() => onOpen(event.slug)} style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 12, overflow: 'hidden', cursor: 'pointer' }}>
      <div style={{ width: '100%', aspectRatio: '4/3', background: '#F1EFE8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {event.cover_image ? (
          <img src={event.cover_image} alt={event.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <span style={{ fontSize: 32, opacity: 0.3 }}>📅</span>
        )}
      </div>
      <div style={{ padding: '0.65rem' }}>
        {event.is_boosted && <span style={{ fontSize: 10, background: '#FFF3C4', color: '#8A6D00', padding: '2px 6px', borderRadius: 8, marginBottom: 4, display: 'inline-block' }}>{t('lbl_sponsored')}</span>}
        {event.matched_interest && <span style={{ fontSize: 10, background: colors.primaryBg, color: colors.primary, padding: '2px 6px', borderRadius: 8, marginBottom: 4, marginLeft: 4, display: 'inline-block' }}>{t('lbl_for_you')}</span>}
        <p style={{ fontSize: 12, color: colors.textMuted, margin: '0 0 2px' }}>{event.group_name}</p>
        <p style={{ fontSize: 13, fontWeight: 500, margin: '0 0 4px', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{event.title}</p>
        <p style={{ fontSize: 12, color: colors.textMuted, margin: '0 0 4px' }}>📆 {formatDate(event.event_date)}</p>
        <p style={{ fontSize: 12, color: colors.textMuted, margin: '0 0 6px' }}>{event.is_online ? '💻 Online' : `📍 ${event.city}`} · {event.attendees_count}/{event.max_attendees} going</p>
        <p style={{ fontSize: 14, fontWeight: 600, color: colors.primary, margin: 0 }}>
          {event.price_pi > 0 ? <PiPrice pi={event.price_pi} /> : 'Free'}
        </p>
        {piRate && event.price_pi > 0 && <p style={{ fontSize: 10, color: colors.textMuted, margin: 0 }}>≈ ${(event.price_pi * piRate).toFixed(2)}</p>}
      </div>
    </div>
  );
}

function EventDetailModal({ slug, onClose, onRsvped }) {
  const [event, setEvent] = useState(null);
  const [rsvping, setRsvping] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    fetch(`${API_BASE}/events/${slug}/`, { headers: getAuthToken() ? authHeaders() : {} }).then((r) => r.json()).then(setEvent).catch(() => {});
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const doRsvp = async () => {
    if (!getAuthToken()) { setMsg(t('login_first')); return; }
    setRsvping(true);
    setMsg('');
    try {
      const res = await fetch(`${API_BASE}/events/${event.id}/rsvp/`, { method: 'POST', headers: authHeaders() });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not RSVP.');

      if (!data.requires_payment) {
        setMsg(`You're ${data.status === 'going' ? 'in!' : 'on the waitlist.'}`);
        setRsvping(false);
        load();
        onRsvped();
        return;
      }

      if (!window.Pi) { setMsg(t('open_pi_pay')); setRsvping(false); return; }
      await window.Pi.authenticate(['username', 'payments'], () => {});
      window.Pi.createPayment(
        { amount: data.price_pi, memo: `RSVP: ${event.title}`, metadata: { rsvp_id: data.rsvp_id } },
        {
          onReadyForServerApproval: (paymentId) => fetch(`${API_BASE}/rsvps/${data.rsvp_id}/approve/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ payment_id: paymentId }) }),
          onReadyForServerCompletion: (paymentId, txid) => {
            fetch(`${API_BASE}/rsvps/${data.rsvp_id}/complete/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ payment_id: paymentId, txid }) })
              .then(() => { setMsg('Payment complete - you\'re in!'); setRsvping(false); load(); onRsvped(); })
              .catch(() => { setMsg('Payment reached Pi but RSVP activation failed.'); setRsvping(false); });
          },
          onCancel: () => { setMsg(t('pay_cancelled')); setRsvping(false); },
          onError: () => { setMsg(t('pay_error')); setRsvping(false); },
        }
      );
    } catch (e) {
      setMsg(e.message);
      setRsvping(false);
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px 12px', overflowY: 'auto' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 16, maxWidth: 560, width: '100%', padding: '1.5rem', marginTop: 20 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer', color: colors.textMuted }}>✕</button>
        </div>
        {!event ? <p style={{ textAlign: 'center', color: colors.textMuted }}>{t('loading')}</p> : (
          <>
            {event.cover_image && <img src={event.cover_image} alt={event.title} style={{ width: '100%', borderRadius: 10, marginBottom: 12 }} />}
            <p style={{ fontSize: 12, color: colors.primary, margin: '0 0 4px' }}>{event.group_name}</p>
            <h2 style={{ fontSize: 18, margin: '0 0 8px' }}>{event.title}</h2>
            <p style={{ fontSize: 13, color: colors.textMuted, margin: '0 0 4px' }}>📆 {formatDate(event.event_date)} ({event.duration_minutes} min)</p>
            <p style={{ fontSize: 13, color: colors.textMuted, margin: '0 0 12px' }}>
              {event.is_online ? (event.online_link ? <a href={event.online_link} style={{ color: colors.primary }}>{t('meh_join_online')}</a> : '💻 Online event') : `📍 ${event.location}, ${event.city}`}
            </p>
            <p style={{ fontSize: 14, lineHeight: 1.5, marginBottom: 12 }}>{event.description}</p>
            <p style={{ fontSize: 13, color: colors.textMuted, marginBottom: 12 }}>{event.attendees_count}/{event.max_attendees} attending · Hosted by @{event.organizer_username}</p>

            <p style={{ fontSize: 16, fontWeight: 600, color: colors.primary, marginBottom: 12 }}>{event.price_pi > 0 ? <PiPrice pi={event.price_pi} /> : 'Free event'}</p>

            {event.my_rsvp_status ? (
              <p style={{ fontSize: 13, color: colors.primary, textAlign: 'center', padding: 10, background: colors.primaryBg, borderRadius: 8 }}>
                ✅ You're {event.my_rsvp_status === 'going' ? 'going!' : event.my_rsvp_status}
              </p>
            ) : (
              <button
                onClick={() => {
                  if (event.price_pi > 0) {
                    fetch('/api/cart/add/', {
                      method: 'POST', headers: authHeaders(),
                      body: JSON.stringify({
                        app_name: 'meetup', item_type: 'event', reference_id: event.id,
                        title: event.title, thumbnail_url: event.cover_image || '', price_pi: event.price_pi, metadata: {},
                      }),
                    }).then(() => setMsg('Added to cart! View it from the 🛒 My Cart menu.'));
                  } else {
                    doRsvp();
                  }
                }}
                disabled={rsvping}
                style={{ width: '100%', padding: '12px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}
              >
                {rsvping ? 'Processing...' : event.price_pi > 0 ? `🛒 Add to Cart - $<PiPrice pi={event.price_pi} />` : 'RSVP - Free'}
              </button>
            )}
            {msg && <p style={{ fontSize: 12, color: colors.danger, marginTop: 8 }}>{msg}</p>}

            {event.reviews?.length > 0 && (
              <div style={{ marginTop: 16, borderTop: `0.5px solid ${colors.border}`, paddingTop: 12 }}>
                <p style={{ fontSize: 13, fontWeight: 600, margin: '0 0 8px' }}>{t('reviews')}</p>
                {event.reviews.map((r, i) => (
                  <p key={i} style={{ fontSize: 12, color: colors.textMuted, margin: '4px 0' }}>@{r.reviewer} {'⭐'.repeat(r.rating)} {r.comment}</p>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function GroupsPanel() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', category: 'other', description: '', city: '' });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/groups/`);
      const data = await safeJson(res);
      setGroups(data.groups || []);
    } catch (e) {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const joinGroup = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/groups/${id}/join/`, { method: 'POST', headers: authHeaders() });
      if (!res.ok) { alert(t('login_first')); return; }
      alert('Joined!');
      load();
    } catch (e) { alert('Could not join.'); }
  };

  const createGroup = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/groups/create/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(form) });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not create group.');
      alert('Group created!');
      setForm({ name: '', category: 'other', description: '', city: '' });
      load();
    } catch (e) { alert(networkMessage(e)); } finally { setSubmitting(false); }
  };

  const inputStyle = { width: '100%', padding: '8px 10px', fontSize: 14, border: '0.5px solid #B4B2A9', borderRadius: 6, boxSizing: 'border-box' };
  const labelStyle = { fontSize: 13, color: colors.textMuted, display: 'block', marginBottom: 4 };

  return (
    <div>
      <div style={{ background: '#F1EFE8', border: `0.5px solid ${colors.border}`, borderRadius: 12, padding: '1.25rem', marginBottom: '1.5rem' }}>
        <p style={{ fontWeight: 500, fontSize: 15, margin: '0 0 12px' }}>{t('start_group')}</p>
        <div style={{ display: 'grid', gap: 10, marginBottom: 10 }}>
          <div><label style={labelStyle}>{t('group_name')}</label><input style={inputStyle} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Lahore Tech Meetup" /></div>
          <div><label style={labelStyle}>{t('category_label')}</label>
            <select style={inputStyle} value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
              {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div><label style={labelStyle}>{t('city')}</label><input style={inputStyle} value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} /></div>
          <div><label style={labelStyle}>{t('description')}</label><textarea style={{ ...inputStyle, resize: 'vertical' }} rows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} /></div>
        </div>
        <button onClick={createGroup} disabled={submitting || !form.name} style={{ width: '100%', padding: '10px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 6, background: colors.primary, color: '#fff', cursor: 'pointer' }}>
          {submitting ? 'Creating...' : 'Create group'}
        </button>
      </div>

      {loading ? <p style={{ color: colors.textMuted, textAlign: 'center' }}>{t('loading_groups')}</p> :
        groups.length === 0 ? <p style={{ color: colors.textMuted, textAlign: 'center' }}>{t('no_groups_yet')}</p> :
        groups.map((g) => (
          <div key={g.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 10, padding: '0.75rem', marginBottom: 8 }}>
            <div>
              <p style={{ fontSize: 14, fontWeight: 500, margin: 0 }}>{g.name}</p>
              <p style={{ fontSize: 12, color: colors.textMuted, margin: 0 }}>{g.city} · {g.members_count} members · {g.upcoming_events} upcoming events</p>
            </div>
            <button onClick={() => joinGroup(g.id)} style={{ fontSize: 12, padding: '6px 12px', border: 'none', borderRadius: 6, background: colors.primaryBg, color: colors.primary, cursor: 'pointer' }}>{t('join_btn')}</button>
          </div>
        ))
      }
    </div>
  );
}

function CreateEventForm({ onCreated }) {
  const [myGroups, setMyGroups] = useState([]);
  const [form, setForm] = useState({ group_id: '', title: '', description: '', is_online: false, location: '', city: '', online_link: '', event_date: '', duration_minutes: 60, price_pi: 0, max_attendees: 50 });
  const [imageFile, setImageFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/groups/mine/`, { headers: authHeaders() }).then((r) => r.json()).then((data) => {
      setMyGroups(data.organized || []);
      if (data.organized?.length) setForm((f) => ({ ...f, group_id: data.organized[0].id }));
    }).catch(() => {});
  }, []);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const inputStyle = { width: '100%', padding: '8px 10px', fontSize: 14, border: '0.5px solid #B4B2A9', borderRadius: 6, boxSizing: 'border-box' };
  const labelStyle = { fontSize: 13, color: colors.textMuted, display: 'block', marginBottom: 4 };

  const submit = async () => {
    if (!form.group_id) { alert('Create a group first, in the Groups tab.'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/events/create/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(form) });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not create event.');

      if (imageFile) {
        const fd = new FormData();
        fd.append('image', imageFile);
        const token = getAuthToken();
        await fetch(`${API_BASE}/events/${data.id}/upload-image/`, { method: 'POST', headers: token ? { Authorization: `Token ${token}` } : {}, body: fd });
      }
      alert('Event created!');
      onCreated();
    } catch (e) { alert(networkMessage(e)); } finally { setSubmitting(false); }
  };

  return (
    <div style={{ background: '#F1EFE8', border: `0.5px solid ${colors.border}`, borderRadius: 12, padding: '1.25rem' }}>
      <p style={{ fontWeight: 500, fontSize: 15, margin: '0 0 12px' }}>{t('create_event')}</p>
      {myGroups.length === 0 ? (
        <p style={{ fontSize: 13, color: colors.textMuted }}>{t('meh_group_first')}</p>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          <div><label style={labelStyle}>{t('group_label')}</label>
            <select style={inputStyle} value={form.group_id} onChange={update('group_id')}>
              {myGroups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>
          <div><label style={labelStyle}>{t('event_title')}</label><input style={inputStyle} value={form.title} onChange={update('title')} /></div>
          <div><label style={labelStyle}>{t('description')}</label><textarea style={{ ...inputStyle, resize: 'vertical' }} rows={2} value={form.description} onChange={update('description')} /></div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input type="checkbox" checked={form.is_online} onChange={update('is_online')} /> This is an online event
          </label>
          {form.is_online ? (
            <div><label style={labelStyle}>{t('online_link')}</label><input style={inputStyle} value={form.online_link} onChange={update('online_link')} placeholder="https://zoom.us/..." /></div>
          ) : (
            <>
              <div><label style={labelStyle}>{t('location_label')}</label><input style={inputStyle} value={form.location} onChange={update('location')} /></div>
              <div><label style={labelStyle}>{t('city')}</label><input style={inputStyle} value={form.city} onChange={update('city')} /></div>
            </>
          )}
          <div><label style={labelStyle}>{t('meh_datetime')}</label><input type="datetime-local" style={inputStyle} value={form.event_date} onChange={update('event_date')} /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div><label style={labelStyle}>{t('meh_duration')}</label><input type="number" style={inputStyle} value={form.duration_minutes} onChange={update('duration_minutes')} /></div>
            <div><label style={labelStyle}>{t('max_attendees')}</label><input type="number" style={inputStyle} value={form.max_attendees} onChange={update('max_attendees')} /></div>
          </div>
          <div><label style={labelStyle}>{t('price_pi_free')}</label><input type="number" step="0.01" style={inputStyle} value={form.price_pi} onChange={update('price_pi')} /></div>
          <div><label style={labelStyle}>{t('cover_photo')}</label><input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files[0])} /></div>
        </div>
      )}
      <button onClick={submit} disabled={submitting || !form.title || myGroups.length === 0} style={{ width: '100%', padding: '10px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 6, background: colors.primary, color: '#fff', cursor: 'pointer', marginTop: 12 }}>
        {submitting ? 'Creating...' : 'Publish event'}
      </button>
    </div>
  );
}

function MyEventsPanel() {
  const [events, setEvents] = useState(null);
  const [boosting, setBoosting] = useState({});

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/events/mine/`, { headers: authHeaders() });
      if (res.ok) setEvents((await safeJson(res)).events);
    } catch (e) {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const boostEvent = async (id, title) => {
    if (!window.Pi) { alert(t('open_pi_pay2')); return; }
    setBoosting((b) => ({ ...b, [id]: true }));
    try {
      await window.Pi.authenticate(['username', 'payments'], () => {});
      window.Pi.createPayment(
        { amount: 1, memo: `Boost event: ${title}`, metadata: { event_id: id } },
        {
          onReadyForServerApproval: (paymentId) => fetch(`${API_BASE}/events/${id}/boost/approve/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ payment_id: paymentId }) }),
          onReadyForServerCompletion: (paymentId, txid) => {
            fetch(`${API_BASE}/events/${id}/boost/complete/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ payment_id: paymentId, txid, amount: 1, days: 7 }) })
              .then(() => { alert(t('msg_boost7')); setBoosting((b) => ({ ...b, [id]: false })); load(); })
              .catch(() => { alert(t('msg_boost_fail')); setBoosting((b) => ({ ...b, [id]: false })); });
          },
          onCancel: () => setBoosting((b) => ({ ...b, [id]: false })),
          onError: () => setBoosting((b) => ({ ...b, [id]: false })),
        }
      );
    } catch (e) {
      setBoosting((b) => ({ ...b, [id]: false }));
    }
  };

  const viewAttendees = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/events/${id}/attendees/`, { headers: authHeaders() });
      const data = await safeJson(res);
      const text = data.attendees.map((a) => `@${a.attendee_username} - ${a.status}${a.ticket_code ? ` (${a.ticket_code})` : ''}`).join('\n') || 'No attendees yet.';
      alert(text);
    } catch (e) { alert('Could not load attendees.'); }
  };

  return (
    <div>
      {events && events.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, marginBottom: '1.5rem' }}>
          {events.map((e) => (
            <div key={e.id} style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 10, padding: 8 }}>
              <div style={{ width: '100%', aspectRatio: '4/3', background: '#F1EFE8', borderRadius: 6, marginBottom: 6, overflow: 'hidden' }}>
                {e.cover_image && <img src={e.cover_image} alt={e.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
              </div>
              <p style={{ fontSize: 12, fontWeight: 500, margin: '0 0 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.title}</p>
              <p style={{ fontSize: 11, color: colors.textMuted, margin: '0 0 6px' }}>{formatDate(e.event_date)} · {e.attendees_count}/{e.max_attendees}</p>
              <button onClick={() => viewAttendees(e.id)} style={{ width: '100%', padding: '4px', fontSize: 11, border: `0.5px solid ${colors.border}`, borderRadius: 4, background: '#fff', cursor: 'pointer', marginBottom: 4 }}>{t('meh_attendees')}</button>
              <button onClick={() => boostEvent(e.id, e.title)} disabled={boosting[e.id] || e.is_boosted} style={{ width: '100%', padding: '4px', fontSize: 11, fontWeight: 500, border: 'none', borderRadius: 4, background: e.is_boosted ? '#FFF3C4' : colors.primaryBg, color: e.is_boosted ? '#8A6D00' : colors.primary, cursor: e.is_boosted ? 'default' : 'pointer' }}>
                {e.is_boosted ? '📢 Boosted' : boosting[e.id] ? 'Processing...' : '📢 Boost (1 Pi / 7d)'}
              </button>
            </div>
          ))}
        </div>
      )}
      <CreateEventForm onCreated={load} />
    </div>
  );
}

function MyRsvpsPanel() {
  const [rsvps, setRsvps] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/rsvps/mine/`, { headers: authHeaders() });
      if (res.ok) setRsvps((await safeJson(res)).rsvps);
    } catch (e) {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cancelRsvp = async (id) => {
    try {
      await fetch(`${API_BASE}/rsvps/${id}/cancel/`, { method: 'POST', headers: authHeaders() });
      alert('RSVP cancelled.');
      load();
    } catch (e) { alert('Could not cancel.'); }
  };

  const leaveReview = async (id) => {
    const rating = parseInt(prompt('Rate 1-5:', '5'), 10);
    if (!rating) return;
    const comment = prompt('Comment (optional):', '') || '';
    try {
      const res = await fetch(`${API_BASE}/rsvps/${id}/review/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ rating, comment }) });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not submit review.');
      alert(t('msg_thanks_review'));
    } catch (e) { alert(networkMessage(e)); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <button onClick={load} style={{ fontSize: 12, padding: '4px 10px', border: `0.5px solid ${colors.border}`, borderRadius: 6, background: '#fff', cursor: 'pointer' }}>🔄</button>
      </div>
      {loading ? <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('loading')}</p> :
        rsvps.length === 0 ? <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('no_rsvps_yet')}</p> :
        rsvps.map((r) => (
          <div key={r.id} style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 10, padding: '0.75rem', marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 13, fontWeight: 500 }}>{r.event_title}</span>
              <span style={{ fontSize: 12, color: colors.textMuted, textTransform: 'capitalize' }}>{r.status}</span>
            </div>
            <p style={{ fontSize: 12, color: colors.textMuted, margin: '2px 0' }}>{formatDate(r.event_date)} {r.ticket_code && `· 🎟️ ${r.ticket_code}`}</p>
            {r.status === 'going' && (
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <button onClick={() => cancelRsvp(r.id)} style={{ fontSize: 11, padding: '4px 10px', border: `0.5px solid ${colors.border}`, borderRadius: 10, background: '#fff', cursor: 'pointer' }}>{t('cancel')}</button>
                <button onClick={() => leaveReview(r.id)} style={{ fontSize: 11, padding: '4px 10px', border: `0.5px solid ${colors.border}`, borderRadius: 10, background: '#fff', cursor: 'pointer' }}>{t('lbl_review')}</button>
              </div>
            )}
          </div>
        ))
      }
    </div>
  );
}

function WalletPanel() {
  const [balance, setBalance] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [showLedger, setShowLedger] = useState(false);
  const [amount, setAmount] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/wallet/balance/`, { headers: authHeaders() });
      if (res.ok) setBalance(await safeJson(res));
    } catch (e) {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleLedger = async () => {
    if (!showLedger) {
      try {
        const res = await fetch(`${API_BASE}/wallet/ledger/`, { headers: authHeaders() });
        if (res.ok) setLedger((await safeJson(res)).entries);
      } catch (e) {}
    }
    setShowLedger((s) => !s);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/wallet/withdraw/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ amount: parseFloat(amount), pi_wallet_address: walletAddress }) });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not submit request.');
      alert(t('msg_wd_submitted'));
      setAmount(''); setWalletAddress('');
      load();
    } catch (e) { alert(networkMessage(e)); } finally { setSubmitting(false); }
  };

  if (balance === null) return <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('loading_wallet')}</p>;

  return (
    <div>
      <div style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 12, padding: '1rem', marginBottom: '1.5rem' }}>
        <p style={{ fontSize: 13, color: colors.textMuted, margin: '0 0 4px' }}>{t('available_to_withdraw')}</p>
        <p style={{ fontSize: 26, fontWeight: 600, margin: '0 0 4px', color: colors.primary }}><PiPrice pi={balance.available_balance_pi} /></p>
        <p style={{ fontSize: 12, color: colors.textMuted, margin: '0 0 8px' }}>{t('earned_after_fee')} <PiPrice pi={balance.total_earned_pi} /></p>
        <button onClick={toggleLedger} style={{ fontSize: 12, padding: '4px 10px', border: `0.5px solid ${colors.border}`, borderRadius: 6, background: '#fff', cursor: 'pointer' }}>{showLedger ? 'Hide' : 'View'} ledger</button>
        {showLedger && (
          <div style={{ marginTop: 10, borderTop: `0.5px solid ${colors.border}`, paddingTop: 10 }}>
            {ledger.length === 0 ? <p style={{ fontSize: 12, color: colors.textMuted }}>{t('no_paid_rsvps')}</p> :
              ledger.map((e) => (
                <div key={e.rsvp_id} style={{ fontSize: 12, padding: '6px 0', borderBottom: `0.5px solid ${colors.border}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>{e.event_title} · @{e.attendee}</span>
                    <span><PiPrice pi={e.net_pi} /> net</span>
                  </div>
                </div>
              ))
            }
          </div>
        )}
      </div>

      <div style={{ background: '#F1EFE8', border: `0.5px solid ${colors.border}`, borderRadius: 12, padding: '1.25rem' }}>
        <p style={{ fontWeight: 500, fontSize: 14, margin: '0 0 12px' }}>{t('request_withdrawal')}</p>
        <div style={{ display: 'grid', gap: 10, marginBottom: 10 }}>
          <input type="number" step="0.000001" placeholder={t('amount_pi')} value={amount} onChange={(e) => setAmount(e.target.value)} style={{ padding: '8px 10px', fontSize: 14, border: '0.5px solid #B4B2A9', borderRadius: 6 }} />
          <input type="text" placeholder={t('your_wallet_address')} value={walletAddress} onChange={(e) => setWalletAddress(e.target.value)} style={{ padding: '8px 10px', fontSize: 14, border: '0.5px solid #B4B2A9', borderRadius: 6 }} />
        </div>
        <button onClick={submit} disabled={submitting || !amount || !walletAddress} style={{ width: '100%', padding: '10px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 6, background: colors.primary, color: '#fff', cursor: 'pointer' }}>
          {submitting ? 'Submitting...' : 'Request withdrawal'}
        </button>
      </div>
    </div>
  );
}

function MeetupHelpModal({ onClose }) {
  const sectionStyle = { marginBottom: 20 };
  const headingStyle = { fontSize: 16, fontWeight: 600, margin: '0 0 8px' };
  const textStyle = { fontSize: 14, color: '#3A3935', lineHeight: 1.6, margin: '0 0 8px' };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px 12px', overflowY: 'auto' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 16, maxWidth: 640, width: '100%', padding: '1.5rem', marginTop: 20 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 20 }}>{t('meh_title')}</h2>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer', color: colors.textMuted }}>✕</button>
        </div>

        <div style={sectionStyle}>
          <p style={headingStyle}>{t('flh_what_q')}</p>
          <p style={textStyle}>{t('meh_what')}</p>
        </div>

        <div style={sectionStyle}>
          <p style={headingStyle}>{t('meh_att_q')}</p>
          <p style={textStyle}>{t('meh_a1')}</p>
          <p style={textStyle}>{t('meh_a2')}</p>
          <p style={textStyle}>{t('meh_a3')}</p>
          <p style={textStyle}>{t('meh_a4')}</p>
        </div>

        <div style={sectionStyle}>
          <p style={headingStyle}>{t('meh_org_q')}</p>
          <p style={textStyle}>{t('meh_o1')}</p>
          <p style={textStyle}>{t('meh_o2')}</p>
          <p style={textStyle}>{t('meh_o3')}</p>
          <p style={textStyle}>{t('meh_o4')}</p>
          <p style={textStyle}>{t('meh_o5')}</p>
        </div>

        <div style={sectionStyle}>
          <p style={headingStyle}>{t('meh_fees_q')}</p>
          <p style={textStyle}>{t('meh_fees')}</p>
        </div>

        <div style={sectionStyle}>
          <p style={headingStyle}>{t('meh_faq_q')}</p>
          <p style={{ ...textStyle, fontWeight: 600, marginBottom: 2 }}>{t('meh_q1')}</p>
          <p style={textStyle}>{t('meh_a_q1')}</p>
          <p style={{ ...textStyle, fontWeight: 600, marginBottom: 2 }}>{t('meh_q2')}</p>
          <p style={textStyle}>{t('meh_a_q2')}</p>
          <p style={{ ...textStyle, fontWeight: 600, marginBottom: 2 }}>{t('meh_q3')}</p>
          <p style={textStyle}>{t('meh_a_q3')}</p>
        </div>

        <button onClick={onClose} style={{ width: '100%', padding: '10px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>
          Got it
        </button>
      </div>
    </div>
  );
}

export default function MeetupMarketplace() {
  const [tab, setTab] = useState('browse');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [personalized, setPersonalized] = useState(false);
  const [openSlug, setOpenSlug] = useState(null);
  const [piRate, setPiRate] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (cityFilter) params.set('city', cityFilter);
      const res = await fetch(`${API_BASE}/events/?${params.toString()}`, { headers: getAuthToken() ? authHeaders() : {} });
      const data = await safeJson(res);
      setEvents(data.events || []);
      setPersonalized(!!data.personalized);
    } catch (e) {} finally { setLoading(false); }
  }, [search, cityFilter]);

  useEffect(() => { loadEvents(); }, [loadEvents]);
  useEffect(() => {
    fetch('https://api.coingecko.com/api/v3/simple/price?ids=pi-network-iou&vs_currencies=usd')
      .then((r) => r.json())
      .then((data) => { if (data['pi-network-iou']?.usd) setPiRate(data['pi-network-iou'].usd); })
      .catch(() => {});
  }, []);

  const tabStyle = (t) => ({ padding: '8px 14px', fontSize: 13, fontWeight: 500, border: 'none', borderBottom: tab === t ? `2px solid ${colors.primary}` : '2px solid transparent', background: 'transparent', color: tab === t ? colors.primary : colors.textMuted, cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' });

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '1.5rem', fontFamily: 'sans-serif', background: colors.bg, minHeight: '100vh' }}>
      {showHelp && <MeetupHelpModal onClose={() => setShowHelp(false)} />}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ fontSize: 22, margin: '0 0 4px' }}>{t('meh_app')}</h1>
          <p style={{ fontSize: 13, color: colors.textMuted, margin: '0 0 1rem' }}>{t('meetup_tagline')}</p>
        </div>
        <button onClick={() => setShowHelp(true)} style={{ padding: '6px 14px', fontSize: 13, fontWeight: 500, border: `0.5px solid ${colors.border}`, borderRadius: 20, background: '#fff', cursor: 'pointer', flexShrink: 0 }}>
          ❓ How it works
        </button>
      </div>

      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', borderBottom: `0.5px solid ${colors.border}`, marginBottom: '1.25rem' }}>
        <button style={tabStyle('browse')} onClick={() => setTab('browse')}>{t('events_tab')}</button>
        <button style={tabStyle('groups')} onClick={() => setTab('groups')}>{t('groups_tab')}</button>
        <button style={tabStyle('myevents')} onClick={() => setTab('myevents')}>{t('my_events')}</button>
        <button style={tabStyle('rsvps')} onClick={() => setTab('rsvps')}>{t('my_rsvps')}</button>
        <button style={tabStyle('wallet')} onClick={() => setTab('wallet')}>{t('wallet')}</button>
      </div>

      {tab === 'browse' && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: '0.75rem' }}>
            <input placeholder={t('meh_search_ph')} value={search} onChange={(e) => setSearch(e.target.value)} style={{ flex: 1, padding: '10px 14px', fontSize: 14, border: `0.5px solid #B4B2A9`, borderRadius: 20, boxSizing: 'border-box' }} />
            <input placeholder={t('city')} value={cityFilter} onChange={(e) => setCityFilter(e.target.value)} style={{ width: 110, padding: '10px 14px', fontSize: 14, border: `0.5px solid #B4B2A9`, borderRadius: 20, boxSizing: 'border-box' }} />
            <button onClick={loadEvents} style={{ padding: '0 14px', fontSize: 13, border: `0.5px solid ${colors.border}`, borderRadius: 20, background: '#fff', cursor: 'pointer' }}>🔄</button>
          </div>

          {personalized && <p style={{ fontSize: 12, color: colors.primary, margin: '0 0 0.75rem' }}>✨ {t('sorted_interests')}</p>}

          {loading ? <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('loading_events')}</p> :
            events.length === 0 ? <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('no_upcoming_events')}</p> : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
                {events.map((e) => <EventCard key={e.id} event={e} onOpen={setOpenSlug} piRate={piRate} />)}
              </div>
            )
          }

          {openSlug && <EventDetailModal slug={openSlug} onClose={() => setOpenSlug(null)} onRsvped={() => {}} />}
        </>
      )}

      {tab === 'groups' && <GroupsPanel />}
      {tab === 'myevents' && <MyEventsPanel />}
      {tab === 'rsvps' && <MyRsvpsPanel />}
      {tab === 'wallet' && <WalletPanel />}
    </div>
  );
}
